/**
 * storyboard-director.js
 * Đạo diễn Hoạt Cảnh Storyboard Động cho Voxel Anime Studio.
 * Quản lý đa nhân vật, diễn hoạt phân cảnh (Shots), lời thoại TTS, camera điện ảnh,
 * hiệu ứng cảm xúc 3D (Emotion FX) và chuyển động âm thanh.
 */

import * as THREE from 'three';
import { BlockyAvatar } from '../avatars/blocky-avatar.js';
import { BlockyAnimal } from '../avatars/blocky-animal.js';
import { StreetTeaStall } from '../city/street-tea-stall.js';
import { EmotionEffectsManager } from './emotion-effects.js';
import { SceneAudioManager } from '../audio/scene-audio-manager.js';
import { SfxPlayer } from '../audio/sfx.js';

export class StoryboardDirector {
  /**
   * @param {THREE.Scene} scene
   * @param {THREE.PerspectiveCamera} camera
   * @param {OrbitControls} controls
   * @param {CharacterManager} characterManager
   * @param {EmotionEffectsManager} emotionManager
   */
  constructor(scene, camera, controls, characterManager, emotionManager = null) {
    this.scene = scene;
    this.camera = camera;
    this.controls = controls;
    this.characterManager = characterManager;
    this.emotionManager = emotionManager || new EmotionEffectsManager();

    this.group = new THREE.Group();
    this.group.name = 'StoryboardDirectorGroup';

    this.storyboard = [];
    this.currentTime = 0;
    this.isPlaying = false;
    this.cameraMode = 'cinematic'; // 'cinematic' | 'free'

    this.isSoundEnabled = true;
    this.isTtsEnabled = true;
    this.audioManager = new SceneAudioManager({ isEnabled: this.isTtsEnabled });
    this.audioCtx = null;
    this.currentShotIndex = -1;
    this.lastSpokenShotId = null;

    // Rung lắc camera (Camera Shake)
    this.shakeIntensity = 0;

    // Bộ nhớ đệm các nhân vật đã khởi tạo trong scene
    this.actorCache = new Map(); // actorId -> BlockyAvatar

    // Điểm ngắm và vị trí mục tiêu cho Camera
    this.targetCamPos = new THREE.Vector3(0, 2.2, 5.0);
    this.targetLookAt = new THREE.Vector3(0, 1.2, -4.5);

    // Callbacks thông báo trạng thái lên UI
    this.onProgressCallbacks = [];

    // Tọa độ các điểm đặt nhân vật (Waypoints) trên vỉa hè & quán nước
    this.waypoints = {
      sidewalk_center: { x: 0.0, y: 0.22, z: -4.5, rotY: 0 },
      sidewalk_left: { x: -3.2, y: 0.22, z: -4.5, rotY: 0.2 },
      sidewalk_right: { x: 3.2, y: 0.22, z: -4.5, rotY: -0.2 },
      tea_stall_bench: { x: 2.2, y: 0.22, z: -4.2, rotY: -0.8 },
      tea_stall_owner: { x: 3.2, y: 0.22, z: -4.8, rotY: 0 }
    };

    this.initAudioContext();
    this.initEnvironmentDecor();
  }

  /**
   * Đặt bối cảnh trang trí cơ bản (quán trà đá)
   */
  initEnvironmentDecor() {
    this.teaStall = new StreetTeaStall();
    this.teaStall.root.position.set(2.8, 0.22, -4.3);
    this.group.add(this.teaStall.root);
  }

  initAudioContext() {
    if (!this.sfx) {
      this.sfx = new SfxPlayer({
        isSoundEnabled: () => this.isSoundEnabled
      });
    } else {
      this.sfx.initAudioContext();
    }
    this.audioCtx = this.sfx.audioCtx;
  }

  resumeAudio() {
    if (this.sfx) this.sfx.resumeAudio();
  }

  // --- ÂM THANH PROCEDURAL SFX (SfxPlayer) ---
  playWhoosh() {
    if (this.sfx) this.sfx.playWhoosh('storyboard');
  }

  playEmotionTing() {
    if (this.sfx) this.sfx.playEmotionTing();
  }

  // --- QUẢN LÝ KỊCH BẢN (STORYBOARD) ---
  /**
   * Nạp danh sách các phân cảnh vào đạo diễn
   * @param {Array<object>} storyboard
   */
  loadStoryboard(storyboard) {
    this.storyboard = Array.isArray(storyboard) ? [...storyboard] : [];
    this.currentTime = 0;
    this.currentShotIndex = -1;
    this.lastSpokenShotId = null;

    // Đảm bảo các nhân vật trong kịch bản đã được khởi tạo
    this.syncActorsWithStoryboard();

    // Nhảy về frame đầu tiên
    this.seek(0, true);
  }

  /**
   * Tính tổng thời lượng của toàn bộ kịch bản
   */
  get totalDuration() {
    if (!this.storyboard.length) return 0;
    return this.storyboard.reduce((acc, shot) => acc + Math.max(shot.duration || 1, 0.5), 0);
  }

  /**
   * Đồng bộ dàn diễn viên với danh sách cần dùng trong kịch bản
   */
  syncActorsWithStoryboard() {
    // Ẩn hoặc dọn dẹp các avatar không còn trong kịch bản
    const neededActorIds = new Set(this.storyboard.map((s) => s.actorId).filter(Boolean));

    neededActorIds.forEach((actorId) => {
      if (!this.actorCache.has(actorId)) {
        const charProfile = this.characterManager.getCharacter(actorId);
        if (charProfile) {
          const initParams = this.characterManager.toAvatarInitParams(charProfile);
          let avatar;
          if (initParams.type === 'animal' || charProfile.type === 'animal') {
            avatar = new BlockyAnimal(
              initParams.speciesKey || charProfile.speciesKey || 'DOG',
              initParams.variantKey || charProfile.variantKey,
              { scale: initParams.scale || charProfile.scale || 1.0 }
            );
          } else {
            avatar = new BlockyAvatar(initParams.presetKey, initParams.options);
          }
          avatar.root.name = `Actor_${actorId}`;
          this.group.add(avatar.root);
          this.actorCache.set(actorId, avatar);
        }
      }
    });

    // Cập nhật vị trí mặc định cho các diễn viên
    this.storyboard.forEach((shot) => {
      const avatar = this.actorCache.get(shot.actorId);
      if (avatar) {
        const wp = this.resolvePosition(shot.spawnPoint || shot.position);
        avatar.root.position.set(wp.x, wp.y, wp.z);
        avatar.root.rotation.y = wp.rotY || 0;
        avatar.root.visible = true;
      }
    });
  }

  resolvePosition(posOrPreset) {
    if (typeof posOrPreset === 'string' && this.waypoints[posOrPreset]) {
      return this.waypoints[posOrPreset];
    }
    if (posOrPreset && typeof posOrPreset === 'object') {
      return {
        x: posOrPreset.x ?? 0,
        y: posOrPreset.y ?? 0.22,
        z: posOrPreset.z ?? -4.5,
        rotY: posOrPreset.rotY ?? 0
      };
    }
    return this.waypoints.sidewalk_center;
  }

  /**
   * Xác định phân cảnh nào đang phát tại thời điểm time
   * @param {number} time
   * @returns {{ shotIndex: number, shot: object|null, localTime: number }}
   */
  getShotAtTime(time) {
    if (!this.storyboard.length) return { shotIndex: -1, shot: null, localTime: 0 };

    let accumulated = 0;
    for (let i = 0; i < this.storyboard.length; i++) {
      const shot = this.storyboard[i];
      const dur = Math.max(shot.duration || 1, 0.5);
      if (time >= accumulated && time < accumulated + dur) {
        return { shotIndex: i, shot, localTime: time - accumulated };
      }
      accumulated += dur;
    }

    // Nếu thời gian vượt quá tổng thời lượng, trả về shot cuối
    const lastIdx = this.storyboard.length - 1;
    return {
      shotIndex: lastIdx,
      shot: this.storyboard[lastIdx],
      localTime: this.storyboard[lastIdx].duration || 1
    };
  }

  // --- ĐIỀU KHIỂN PHÁT (PLAYBACK) ---
  play() {
    this.resumeAudio();
    this.isPlaying = true;
  }

  pause() {
    this.isPlaying = false;
    this.stopTts();
  }

  restart() {
    this.seek(0, true);
    this.play();
  }

  seek(targetTime, force = false) {
    const total = this.totalDuration;
    this.currentTime = Math.max(0, Math.min(targetTime, total));

    const { shotIndex, shot } = this.getShotAtTime(this.currentTime);

    if (shotIndex !== this.currentShotIndex || force) {
      this.currentShotIndex = shotIndex;
      if (shot) {
        this.applyShot(shot, force);
      }
    }

    this.notifyProgress();
  }

  /**
   * Áp dụng trạng thái của một Shot: Vị trí, Cử động, Biểu cảm, Camera, Lời thoại
   * @param {object} shot
   * @param {boolean} instant - nếu true sẽ cắt góc máy tức thì thay vì lerp
   */
  applyShot(shot, instant = false) {
    if (!shot) return;

    // 1. Tìm diễn viên chính của Shot
    const avatar = this.actorCache.get(shot.actorId);
    let actorPos = new THREE.Vector3(0, 0.22, -4.5);

    if (avatar) {
      const wp = this.resolvePosition(shot.spawnPoint || shot.position);
      avatar.root.position.set(wp.x, wp.y, wp.z);
      avatar.root.rotation.y = wp.rotY || 0;
      actorPos = avatar.root.position.clone();

      // Cử động hoạt họa
      if (shot.action) {
        avatar.playAction(shot.action);
      }

      // Biểu cảm khuôn mặt
      if (shot.expression && typeof avatar.setExpression === 'function') {
        avatar.setExpression(shot.expression);
      }

      // Hiệu ứng cảm xúc Anime 3D
      if (this.emotionManager) {
        this.emotionManager.attachEmotion(avatar, shot.emotionFx || 'none');
        if (shot.emotionFx && shot.emotionFx !== 'none') {
          this.playEmotionTing();
        }
      }
    }

    // 2. Định vị Camera theo Preset
    this.calculateCameraPreset(shot.camera?.preset || 'medium', actorPos, shot.camera?.shake || 0, instant);

    // 3. Hiệu ứng âm thanh chuyển cảnh
    this.playWhoosh();

    // 4. Lời thoại & Giọng đọc TTS
    if (this.isPlaying && this.isTtsEnabled && shot.dialogue && this.lastSpokenShotId !== shot.id) {
      this.lastSpokenShotId = shot.id;
      this.speakDialogue(shot.dialogue, shot.voice, shot.actorId, shot.id);
    }
  }

  /**
   * Tính toán tọa độ và điểm ngắm cho Camera theo từng phong cách điện ảnh
   */
  calculateCameraPreset(preset, actorPos, shake = 0, instant = false) {
    this.shakeIntensity = shake;

    let targetPos = new THREE.Vector3();
    let lookTarget = new THREE.Vector3(actorPos.x, actorPos.y + 1.2, actorPos.z);

    switch (preset) {
      case 'close_up':
        // Cận cảnh biểu cảm gương mặt
        targetPos.set(actorPos.x + 0.35, actorPos.y + 1.45, actorPos.z + 1.6);
        lookTarget.set(actorPos.x, actorPos.y + 1.35, actorPos.z);
        break;

      case 'wide':
        // Toàn cảnh không gian vỉa hè đường phố
        targetPos.set(0.0, 3.2, 8.5);
        lookTarget.set(0.0, 1.2, -4.5);
        break;

      case 'over_shoulder':
        // Góc máy qua vai đối thoại kinh điển
        targetPos.set(actorPos.x - 1.2, actorPos.y + 1.5, actorPos.z - 0.8);
        lookTarget.set(actorPos.x + 1.5, actorPos.y + 1.2, actorPos.z);
        break;

      case 'dutch_dramatic':
        // Góc nghiêng kịch tính từ dưới lên
        targetPos.set(actorPos.x - 0.9, actorPos.y + 0.6, actorPos.z + 2.2);
        lookTarget.set(actorPos.x, actorPos.y + 1.4, actorPos.z);
        break;

      case 'bird_eye':
        // Góc mắt chim nhìn từ trên cao xuống
        targetPos.set(actorPos.x, actorPos.y + 5.5, actorPos.z + 2.5);
        lookTarget.set(actorPos.x, actorPos.y, actorPos.z);
        break;

      case 'medium':
      default:
        // Trung cảnh ngang ngực
        targetPos.set(actorPos.x, actorPos.y + 1.4, actorPos.z + 3.2);
        lookTarget.set(actorPos.x, actorPos.y + 1.15, actorPos.z);
        break;
    }

    this.targetCamPos.copy(targetPos);
    this.targetLookAt.copy(lookTarget);

    if (instant && this.cameraMode === 'cinematic') {
      this.camera.position.copy(targetPos);
      this.controls.target.copy(lookTarget);
      this.camera.lookAt(lookTarget);
    }
  }

  // --- LỒNG TIẾNG TTS (EDGE-TTS MẶC ĐỊNH & MP3 OVERRIDE) ---
  speakDialogue(text, voiceCfg = {}, actorId = '', shotId = '') {
    if (!this.isPlaying || !this.isTtsEnabled) return;
    if (this.audioManager) {
      this.audioManager.setEnabled(this.isTtsEnabled);
      this.audioManager.playCue({
        cueId: shotId || `shot_${this.currentShotIndex}`,
        text,
        speaker: actorId,
        characterId: actorId,
        voice: voiceCfg?.voice,
        rate: voiceCfg?.rate,
        pitch: voiceCfg?.pitch,
        customMp3Url: voiceCfg?.customMp3Url
      });
    }
  }

  stopTts() {
    if (this.audioManager) {
      this.audioManager.stop();
    }
  }

  // --- CALLBACKS VÀ CẬP NHẬT GIAO DIỆN ---
  onProgress(cb) {
    if (typeof cb === 'function') {
      this.onProgressCallbacks.push(cb);
    }
  }

  notifyProgress() {
    const total = this.totalDuration;
    const { shotIndex, shot } = this.getShotAtTime(this.currentTime);

    const info = {
      currentTime: this.currentTime,
      totalDuration: total,
      activeShotIndex: shotIndex,
      activeShot: shot,
      isPlaying: this.isPlaying,
      cameraMode: this.cameraMode
    };

    this.onProgressCallbacks.forEach((cb) => {
      try {
        cb(info);
      } catch {}
    });
  }

  // --- VÒNG LẶP UPDATE (RENDER LOOP HOOK) ---
  update(delta) {
    const d = Math.min(delta, 0.1);

    // 1. Cập nhật Emotion FX
    if (this.emotionManager) {
      this.emotionManager.update(d);
    }

    // 2. Cập nhật animation cho toàn bộ các diễn viên
    this.actorCache.forEach((avatar) => {
      if (avatar && avatar.update) {
        avatar.update(d);
      }
    });

    // 3. Tiến trình phát kịch bản
    if (this.isPlaying) {
      this.currentTime += d;
      const total = this.totalDuration;

      if (this.currentTime >= total) {
        this.currentTime = total;
        this.isPlaying = false;
        this.stopTts();
        this.notifyProgress();
        return;
      }

      const { shotIndex, shot } = this.getShotAtTime(this.currentTime);
      if (shotIndex !== this.currentShotIndex) {
        this.currentShotIndex = shotIndex;
        if (shot) {
          this.applyShot(shot, false);
        }
      }

      this.notifyProgress();
    }

    // 4. Xử lý Camera Cinematic Lerp & Shake
    if (this.cameraMode === 'cinematic') {
      const lerpSpeed = 0.08;
      this.camera.position.lerp(this.targetCamPos, lerpSpeed);
      this.controls.target.lerp(this.targetLookAt, lerpSpeed);
      this.camera.lookAt(this.controls.target);

      // Camera Shake rung chấn
      if (this.shakeIntensity > 0.001) {
        const prefersReducedMotion =
          typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (!prefersReducedMotion) {
          const shakeX = (Math.random() - 0.5) * this.shakeIntensity;
          const shakeY = (Math.random() - 0.5) * this.shakeIntensity;
          this.camera.position.x += shakeX;
          this.camera.position.y += shakeY;
        }
        this.shakeIntensity *= 0.92; // suy giảm dần
      }
    }
  }

  dispose() {
    this.stopTts();
    if (this.audioManager) {
      this.audioManager.dispose();
    }
    this.actorCache.forEach((avatar) => {
      if (avatar && avatar.dispose) avatar.dispose();
    });
    this.actorCache.clear();
    if (this.emotionManager) this.emotionManager.clearAll();
    if (this.sfx) {
      this.sfx.dispose();
      this.audioCtx = null;
    }
  }
}
